import { computed, DestroyRef, effect, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Urls } from '@helper/urls';
import { Skus } from '@helper/skus';
import { ProductRepositoryService } from '@service/product-repository.service';
import { ShoppingCartData } from '@service/cart-bottom-sheet.service';
import { TagSku } from '@common/sku-tag/sku-tag.component';
import { Cart_Item } from '@proto/store/api/store/cart';
import { Product } from '@proto/store/api/store/product';
import { CartModel } from '../cart/cart.model';
import { BaseProductModel } from '../product/base-product.model';
import { StockRemindDialogComponent } from '../favorite/stock-remind-dialog/stock-remind-dialog.component';
import { MetaData } from '@helper/meta-data';
import { Constants } from '@helper/constants';
import { SizeInfo } from '@helper/size-info';

import { Subject } from 'rxjs';
import { isEqual } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class CartDialogModel extends BaseProductModel {
  readonly smallModeCount = 6;
  readonly currSkuId = computed(() =>
    Skus.skuId(Skus.fromId(this.currentState.skuId).productId, this.colorId(), this.currSize()?.size?.id ?? '')
  );
  readonly colorId = computed(() => this.currColor()?.id ?? '');
  readonly colorImages = computed(
    () =>
      new Map(
        this.apiResult().colors.map(c => {
          const id = this.apiResult().meta?.productId ?? '';
          if (id) {
            const size = this.currSize()?.size;
            const skuId = size ? Skus.skuId(id, c.id, size?.id) : '';
            return [c.id, Skus.getImage220(c, skuId && this.skus().includes(skuId) ? size : undefined)];
          }
          return ['', ''];
        })
      ),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly skus = computed(
    () => {
      const skus: string[] = [];
      const id = this.apiResult().meta?.productId;
      if (id) {
        this.apiResult().colors.forEach(c => {
          this.apiResult().sizes.forEach(s => {
            const skuId = Skus.skuId(id, c.id, s.id);
            const inventory = this.apiResult().inventory[skuId];
            if (inventory && (inventory.num > 0 || inventory.stockRemind)) {
              skus.push(skuId);
            }
          });
        });
      }
      return skus;
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly allSizes = computed(
    () => {
      if (this.currColor()) {
        return undefined;
      }
      const sizes = this.skus()
        .map(skuId => {
          const sku = Skus.fromId(skuId);
          const size = this.apiResult().sizes.find(i => i.id === sku.sizeId);
          return size ? new SizeInfo(size, !sku.colorId ? undefined : this.apiResult().inventory[skuId]) : undefined;
        })
        .filter(s => !!s)
        .sort((a, b) => {
          if (a?.inventory?.num === b?.inventory?.num) {
            return a?.inventory?.stockRemind === b?.inventory?.stockRemind ? 0 : b?.inventory?.stockRemind ? 1 : -1;
          }
          return (b?.inventory?.num ?? 0) - (a?.inventory?.num ?? 0);
        });
      const ids: string[] = [];
      const result: SizeInfo[] = [];
      sizes.forEach(s => {
        if (s && !ids.includes(s?.size.id ?? '')) {
          ids.push(s?.size.id ?? '');
          result.push(s);
        }
      });
      return result.sort((a, b) => a.size.id.localeCompare(b.size.id));
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly cartSizes = computed(() => this.allSizes() ?? this.availableSizes(), { equal: (a, b) => isEqual(a, b) });
  readonly currMax = computed(() => this.currSize()?.inventory?.num ?? 1);
  readonly longestSize = computed(() => {
    if (!this.cartSizes().length) {
      return undefined;
    }
    return this.cartSizes()
      .map(s => s.size.name)
      .sort((a, b) => b.length - a.length)[0];
  });
  readonly detailUrl = computed(() => {
    const product = this.apiResult();
    if (!product.meta) {
      return undefined;
    }
    return Urls.fromMeta(product.meta);
  });
  readonly params = computed(() => {
    const product = this.apiResult();
    if (!product.meta) {
      return undefined;
    }
    return Urls.metaToParam(product.meta);
  });
  readonly mode = signal(false); // true for grid mode
  readonly dataUpdated = new Subject<boolean>();
  readonly skuMap = new Map<string, Skus>();
  readonly toGallery = signal(false);

  // for Reopen
  currentState = new ShoppingCartData('', 0, false, undefined, false, false);

  constructor(
    repository: ProductRepositoryService,
    private dg: MatDialog,
    private cart: CartModel
  ) {
    super(dg, repository, cart);
    effect(() => {
      this.currentState.product = this.apiResult();
    });
    effect(() => {
      const colorId = this._selectColor()?.id;
      let sizeName = this._selectSize()?.size.name;
      if (!sizeName) {
        sizeName = this.lastSize();
      } else {
        this.lastSize.set(sizeName);
      }
      if (this._selectColor()?.color?.name) {
        this.lastColorSize.set(
          this.lastSize()
            ? `${this._selectColor()?.color?.name} - ${this.lastSize()}`
            : this._selectColor()?.color?.name
        );
      }
      const size = this.availableSizes().find(s => s.size.name === sizeName) ?? undefined;
      this._selectSize.set(size);
      if (!colorId || !size?.size.name) {
        this.setCount(1);
      } else if (size?.stockRemind) {
        this.setCount(0);
      } else {
        if (this.currentState.amount && this.currentState.skuId === this.currSkuId()) {
          this.setCount(
            Math.min(this.currentState.amount, this.currSize()?.inventory?.num ?? Constants.MAX_CART_ITEM_COUNT)
          );
          this.currentState.amount = 0;
        } else {
          this.setCount(
            Math.min(Math.max(this.count(), 1), this.currSize()?.inventory?.num ?? Constants.MAX_CART_ITEM_COUNT)
          );
        }
      }
    });
    effect(() => this.mode.set(this.apiResult().colors.length <= this.smallModeCount));
  }

  override getMetaByUrl(): MetaData | undefined {
    return undefined;
  }

  cleanUpViewModel(): void {
    this.cleanSelect();
    this.setApiResult(Product.fromPartial({}));
  }

  cleanSelect(): void {
    this.lastSize.set('');
    this._selectColor.set(undefined);
    this._selectSize.set(undefined);
    this._count.set(1);
  }

  getCartDialogData(
    destroyRef: DestroyRef,
    skuId: string, // 比對5碼，相同則不重新載入
    amount: number,
    destroyOnClose: boolean,
    product?: Product, // 有給 product 則以此開啟
    showDetail?: boolean,
    replace?: boolean
  ): void {
    this.currentState = new ShoppingCartData(
      skuId,
      amount,
      destroyOnClose,
      product,
      showDetail ?? false,
      replace ?? false
    );
    const sku = Skus.fromId(skuId);
    if (!sku.colorId && !sku.sizeId) {
      const next = this.skuMap.get(sku.productId);
      sku.colorId = next?.colorId ?? '';
      sku.sizeId = next?.sizeId ?? '';
    }
    if (product && product.meta?.productId) {
      if (this.apiResult().meta?.productId !== product.meta?.productId) {
        // 當開啟 Cart Dialog 與之前選的商品不同, 就把之前選的清空.
        this.cleanSelect();
      }
      this.setApiResult(product);
      if (!this.colorId() && !this.currSize()) {
        this.productUpdated(sku);
      }
    } else if (!this.apiResult().meta?.productId || sku.productId !== this.apiResult().meta?.productId) {
      this.cleanUpViewModel();
      this.getProduct(destroyRef, skuId);
    } else if (amount && skuId === this.currSkuId()) {
      this.setCount(Math.min(amount, this.currSize()?.inventory?.num ?? Constants.MAX_CART_ITEM_COUNT));
      this.currentState.amount = 0;
    } else {
      this.setSku(
        new Skus(sku.productId, sku.colorId || this.colorId(), sku.sizeId || (this.currSize()?.size?.id ?? ''))
      );
    }
  }

  productUpdated(sku: Skus): void {
    this.setSku(sku);
    this.dataUpdated.next(true);
  }

  cartReplace(): void {
    const color = this.currColor();
    const size = this.currSize();
    if (!color || !size?.size || !size?.inventory) {
      return;
    }
    if (this.currSize()?.stockRemind) {
      StockRemindDialogComponent.open(this.dg, this.apiResult().meta, this.currColor(), this.currSize());
      return;
    }
    if (!this._count()) {
      return;
    }
    this.cart.replaceCartItem(
      new TagSku(this.currSkuId(), color, size.size, size.inventory?.num),
      Cart_Item.fromPartial({
        skuId: this.currentState.skuId,
        quantity: this._count(),
      })
    );
  }

  updateSku(skuId?: string): void {
    const sku = Skus.fromId(skuId || this.currSkuId());
    if (sku.colorId || sku.sizeId) {
      this.skuMap.set(sku.productId, sku);
    }
  }

  private setSku(sku: Skus): void {
    const color =
      this.apiResult().colors.length === 1
        ? this.apiResult().colors[0]
        : this.apiResult().colors.find(c => c.id === sku.colorId);
    this._selectColor.set(color);
    const size = this.availableSizes().find(s => s.size.id === sku.sizeId);
    this._selectSize.set(size);
  }
}
